/**
 * DIAS Readiness Scorecard widget.
 *
 * Drops onto the home dashboard for admin/PM users only. One large overall
 * percentage on the left, three bucket bars on the right with pass/total
 * counts and click-through links straight to the relevant module so the PM
 * can fix the gap in two clicks.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, CheckCircle2, AlertTriangle, ArrowRight, Sparkles, Mail, Loader2, Download, Share2, Copy, Trash2, ListChecks, Send } from 'lucide-react';

import QIPSubmitModal from './QIPSubmitModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKET_LINKS = {
  'Equipment Register':    { href: '/equipment',             label: 'Open equipment' },
  'Staff Licensing':       { href: '/people',                label: 'Manage staff' },
  'Radiation Governance':  { href: '/radiation-governance',  label: 'Open governance' },
};

function color(score) {
  if (score >= 90) return '#16A34A';      // green
  if (score >= 70) return '#0EA5E9';      // blue
  if (score >= 50) return '#D97706';      // amber
  return '#DC2626';                       // red
}

export default function DIASReadinessWidget() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dias-readiness`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => {
        // Non-PM users hit this widget only when an admin renders it — the
        // backend gate returns 403, hide gracefully.
        if (e.response?.status === 403) setErr('forbidden');
        else setErr('error');
      });
  }, []);

  const sendDigest = async () => {
    setSending(true); setSentMsg(null);
    try {
      await axios.post(`${API}/dias-readiness/send-digest`, {}, { withCredentials: true });
      setSentMsg({ type: 'success', text: 'Friday digest sent to all admin/PM recipients.' });
    } catch (e) {
      setSentMsg({ type: 'error', text: e.response?.data?.detail || 'Send failed.' });
    }
    setSending(false);
    setTimeout(() => setSentMsg(null), 6000);
  };

  const downloadPack = async () => {
    try {
      const r = await axios.get(`${API}/dias-evidence-pack`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `DIAS_evidence_pack_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      setSentMsg({ type: 'error', text: e.response?.data?.detail || 'Download failed.' });
    }
  };

  const [shareOpen, setShareOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  if (err === 'forbidden' || err === 'error') return null;
  if (!data) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 mb-6 animate-pulse" data-testid="dias-readiness-loading">
        <div className="h-5 w-48 bg-muted rounded mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="h-32 bg-muted/40 rounded-xl" />
          <div className="md:col-span-3 space-y-2">
            <div className="h-8 bg-muted/40 rounded" />
            <div className="h-8 bg-muted/40 rounded" />
            <div className="h-8 bg-muted/40 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const overallColor = color(data.overall_score);

  return (
    <div className="bg-card border border-border rounded-2xl p-5 mb-6" data-testid="dias-readiness-widget">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4" style={{ color: overallColor }} />
          <h3 className="font-['Outfit'] text-base font-semibold text-foreground">DIAS readiness scorecard</h3>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${overallColor}15`, color: overallColor }}>
            {data.overall_score}% audit-ready
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap lg:items-center gap-2 w-full lg:w-auto">
          <Link to="/dias-checklist" data-testid="dias-checklist-link"
            title="Run the auto-validating QIP pre-flight checklist (30+ checks across the 5 DIAS modules)."
            className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            <ListChecks className="w-3 h-3" /> Master checklist
          </Link>
          <button onClick={downloadPack} data-testid="dias-evidence-pack-btn"
            title="Download a ZIP bundle with the cover scorecard + every register (equipment, QA, staff licensing, RSO, EPA, TLD, lead aprons, SOPs) — ready to upload to QIP."
            className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            <Download className="w-3 h-3" /> Evidence pack (ZIP)
          </button>
          <button onClick={() => setSubmitOpen(true)} data-testid="dias-submit-qip-btn"
            title="Email the evidence pack ZIP + scorecard PDF + master-checklist CSV directly to a QIP intake address."
            className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white"
            style={{ backgroundColor: overallColor }}>
            <Send className="w-3 h-3" /> Submit to QIP
          </button>
          <button onClick={() => setShareOpen(true)} data-testid="dias-share-link-btn"
            title="Generate a tokenised read-only URL to email QIP before site visit."
            className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            <Share2 className="w-3 h-3" /> QIP share link
          </button>
          <button onClick={sendDigest} disabled={sending} data-testid="dias-send-digest-btn"
            title="Email this scorecard + licence-expiry list + overdue equipment to every admin/PM. Cron auto-sends every Friday 21:00 UTC."
            className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border disabled:opacity-50"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
            {sending ? 'Sending…' : 'Send digest now'}
          </button>
        </div>
        <span className="text-[10px] text-muted-foreground basis-full text-right">As of {data.as_of}</span>
      </div>

      {sentMsg && (
        <div data-testid="dias-digest-msg"
          className={`rounded-lg border p-2 text-xs mb-3 ${sentMsg.type === 'success'
            ? 'bg-green-500/5 border-green-500/30 text-green-600'
            : 'bg-red-500/5 border-red-500/30 text-red-600'}`}>
          {sentMsg.text}
        </div>
      )}

      {/* Practice identifiers — surfaced inline so Nash sees LSPN/HPI-O at a glance */}
      {(data.practice?.lspn || data.practice?.hpi_o) && (
        <div className="flex items-center gap-3 flex-wrap text-[11px] mb-4 pb-3 border-b border-border" data-testid="dias-practice-ids">
          {data.practice.name && <span className="font-semibold text-foreground">{data.practice.name}</span>}
          {data.practice.lspn && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono"
              style={{ backgroundColor: `${overallColor}10`, color: overallColor }}>
              <span className="font-bold tracking-wider">LSPN</span> {data.practice.lspn}
            </span>
          )}
          {data.practice.hpi_o && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono"
              style={{ backgroundColor: `${overallColor}10`, color: overallColor }}>
              <span className="font-bold tracking-wider">HPI-O</span> {data.practice.hpi_o}
            </span>
          )}
          {data.practice.dias_accreditation_no && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono"
              style={{ backgroundColor: `${overallColor}10`, color: overallColor }}>
              <span className="font-bold tracking-wider">DIAS</span> {data.practice.dias_accreditation_no}
            </span>
          )}
          <Link to="/practice-settings" data-testid="dias-edit-practice-link"
            className="ml-auto text-[10px] text-muted-foreground hover:text-foreground hover:underline">
            edit →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Big overall ring */}
        <div className="flex flex-col items-center justify-center p-3 rounded-xl border border-border bg-background"
          data-testid="dias-overall-score">
          <ScoreRing value={data.overall_score} color={overallColor} />
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-2">QIP entry-level</p>
          <p className="text-xs font-semibold text-foreground">
            {data.buckets.reduce((a, b) => a + b.passed, 0)} of {data.buckets.reduce((a, b) => a + b.total, 0)} checks pass
          </p>
        </div>

        {/* 3 bucket cards */}
        <div className="md:col-span-3 grid grid-cols-1 gap-2">
          {data.buckets.map(b => (
            <BucketRow key={b.label} bucket={b} />
          ))}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Each bucket scores {data.buckets[0].total} sub-checks. Overall = average across the three DIAS entry-level standards.
        </p>
        <Link to="/compliance" className="text-xs font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: overallColor }}>
          Compliance hub <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {shareOpen && <ShareLinkModal onClose={() => setShareOpen(false)} />}
      {submitOpen && <QIPSubmitModal onClose={() => setSubmitOpen(false)} />}
    </div>
  );
}

function ShareLinkModal({ onClose }) {
  const [tokens, setTokens] = useState([]);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('QIP visit');
  const [days, setDays] = useState(30);
  const [copied, setCopied] = useState('');
  const load = async () => {
    const r = await axios.get(`${API}/qip-share/tokens`, { withCredentials: true });
    setTokens(r.data);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/qip-share/tokens`, { label, days_valid: days }, { withCredentials: true });
      load();
    } finally { setCreating(false); }
  };
  const revoke = async (token) => {
    if (!window.confirm('Revoke this share link? It cannot be un-revoked.')) return;
    await axios.delete(`${API}/qip-share/tokens/${token}`, { withCredentials: true });
    load();
  };
  const copy = (token) => {
    const url = `${window.location.origin}/qip-share/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(''), 2000);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="dias-share-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <h3 className="font-['Outfit'] text-lg font-bold mb-1">QIP share links</h3>
        <p className="text-xs text-muted-foreground mb-4">Tokenised read-only URL — no login required. Hand to the QIP assessor before site visit. Revoke any time.</p>

        <div className="grid grid-cols-12 gap-2 mb-4 border-b border-border pb-3">
          <div className="col-span-7">
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Label</label>
            <input value={label} onChange={e => setLabel(e.target.value)}
              data-testid="share-label" placeholder="e.g. QIP pre-visit · Apr 2026"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Valid days</label>
            <input type="number" value={days} onChange={e => setDays(Number(e.target.value) || 30)}
              data-testid="share-days" min="1" max="365"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-3 flex items-end">
            <button onClick={create} disabled={creating} data-testid="share-create-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: '#42CDD7' }}>
              <Share2 className="w-3.5 h-3.5" /> {creating ? 'Creating…' : 'Create link'}
            </button>
          </div>
        </div>

        {tokens.length === 0
          ? <p className="text-xs text-muted-foreground italic py-4 text-center">No share links yet.</p>
          : <div className="space-y-1.5" data-testid="share-list">
            {tokens.map(t => {
              const url = `${window.location.origin}/qip-share/${t.token}`;
              const expired = t.expires_at < new Date().toISOString();
              const isCopied = copied === t.token;
              return (
                <div key={t.token} className={`bg-muted/40 rounded-lg p-3 text-xs ${t.revoked || expired ? 'opacity-50' : ''}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground">
                        {t.label}
                        {t.revoked && <span className="ml-2 text-[9px] font-bold uppercase text-red-500">Revoked</span>}
                        {!t.revoked && expired && <span className="ml-2 text-[9px] font-bold uppercase text-amber-500">Expired</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        Created {new Date(t.created_at).toLocaleDateString('en-AU')} · expires {new Date(t.expires_at).toLocaleDateString('en-AU')} · viewed {t.view_count}×
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground truncate mt-1">{url}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => copy(t.token)} disabled={t.revoked || expired}
                        className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border disabled:opacity-30"
                        style={{ borderColor: '#42CDD740', color: '#42CDD7' }}>
                        <Copy className="w-3 h-3" /> {isCopied ? 'Copied!' : 'Copy URL'}
                      </button>
                      {!t.revoked && (
                        <button onClick={() => revoke(t.token)}
                          className="p-1.5 text-muted-foreground hover:text-red-500" title="Revoke">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Close</button>
        </div>
      </div>
    </div>
  );
}

function BucketRow({ bucket }) {
  const c = color(bucket.score);
  const link = BUCKET_LINKS[bucket.label];
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl p-3 bg-background" data-testid={`dias-bucket-${bucket.label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => setOpen(o => !o)}
              className="text-sm font-semibold text-foreground hover:underline text-left">
              {bucket.label}
            </button>
            <span className="text-xs font-bold tabular-nums" style={{ color: c }}>
              {bucket.score}% · {bucket.passed}/{bucket.total}
            </span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all"
              style={{ width: `${bucket.score}%`, backgroundColor: c }} />
          </div>
        </div>
        {link && (
          <Link to={link.href} data-testid={`dias-bucket-link-${bucket.label.replace(/\s+/g, '-').toLowerCase()}`}
            className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border whitespace-nowrap"
            style={{ borderColor: `${c}40`, color: c }}>
            {link.label} <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      {open && (
        <ul className="mt-3 space-y-1.5 pt-2 border-t border-border">
          {bucket.items.map((it, idx) => (
            <li key={idx} className="flex items-center gap-2 text-xs">
              {it.ok
                ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
              <span className={`flex-1 ${it.ok ? 'text-foreground' : 'text-muted-foreground'}`}>{it.label}</span>
              <span className="text-[10px] text-muted-foreground tabular-nums">{it.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScoreRing({ value, color }) {
  const size = 96, stroke = 8, r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeWidth={stroke}
        fill="transparent" className="text-muted/40" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke}
        fill="transparent" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="50%" y="50%" dy="0.36em" textAnchor="middle"
        className="text-2xl font-bold fill-foreground rotate-90 origin-center"
        transform={`rotate(90 ${size / 2} ${size / 2})`}>
        {value}
      </text>
    </svg>
  );
}
